// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useCallback, useMemo } from 'react';

import {
  type Shipment,
  type ShipmentId,
  type UpdateShipmentBody,
  useEvolveDelete,
  useEvolvePatch,
  useEvolvePost,
} from '@evolvemep/api-ts';

import { useGeneralContext } from 'helpers/useGeneralContext';

export type ShipmentsContextType = {
  markReady: (shipmentId: ShipmentId) => Promise<void>;
  markShipped: (shipmentId: ShipmentId) => Promise<void>;
  markDelivered: (shipmentId: ShipmentId) => Promise<void>;
  revertToDraft: (shipmentId: ShipmentId) => Promise<void>;
  revertToReady: (shipmentId: ShipmentId) => Promise<void>;
  revertToShipped: (shipmentId: ShipmentId) => Promise<void>;
  updateShipment: (shipmentId: ShipmentId, body: UpdateShipmentBody) => Promise<Shipment>;
  deleteShipment: (shipmentId: ShipmentId) => Promise<void>;
  mutationRunning: boolean;
};

const ShipmentsContext = createContext<ShipmentsContextType | undefined>(undefined);

export const ShipmentsProvider = ({ children }: { children: ReactNode }) => {
  const { apiCall: markReadyApiCall, loading: markingReady } = useEvolvePost('shop/shipment/:shipmentId/ready');
  const { apiCall: markShippedApiCall, loading: markingShipped } = useEvolvePost('shop/shipment/:shipmentId/ship');
  const { apiCall: markDeliveredApiCall, loading: markingDelivered } = useEvolvePost(
    'shop/shipment/:shipmentId/deliver',
  );
  const { apiCall: revertToDraftApiCall, loading: revertingToDraft } = useEvolvePost(
    'shop/shipment/:shipmentId/revert-to-draft',
  );
  const { apiCall: revertToReadyApiCall, loading: revertingToReady } = useEvolvePost(
    'shop/shipment/:shipmentId/revert-to-ready',
  );
  const { apiCall: revertToShippedApiCall, loading: revertingToShipped } = useEvolvePost(
    'shop/shipment/:shipmentId/revert-to-shipped',
  );
  const { apiCall: updateShipmentApiCall, loading: saving } = useEvolvePatch('shop/shipment/:shipmentId');
  const { apiCall: deleteShipmentApiCall, loading: deleting } = useEvolveDelete('shop/shipment/:shipmentId');

  const markReady = useCallback<ShipmentsContextType['markReady']>(
    async (shipmentId) => {
      await markReadyApiCall({}, { url: `shop/shipment/${shipmentId}/ready` });
    },
    [markReadyApiCall],
  );
  const markShipped = useCallback<ShipmentsContextType['markShipped']>(
    async (shipmentId) => {
      await markShippedApiCall({}, { url: `shop/shipment/${shipmentId}/ship` });
    },
    [markShippedApiCall],
  );
  const markDelivered = useCallback<ShipmentsContextType['markDelivered']>(
    async (shipmentId) => {
      await markDeliveredApiCall({}, { url: `shop/shipment/${shipmentId}/deliver` });
    },
    [markDeliveredApiCall],
  );
  const revertToDraft = useCallback<ShipmentsContextType['revertToDraft']>(
    async (shipmentId) => {
      await revertToDraftApiCall({}, { url: `shop/shipment/${shipmentId}/revert-to-draft` });
    },
    [revertToDraftApiCall],
  );
  const revertToReady = useCallback<ShipmentsContextType['revertToReady']>(
    async (shipmentId) => {
      await revertToReadyApiCall({}, { url: `shop/shipment/${shipmentId}/revert-to-ready` });
    },
    [revertToReadyApiCall],
  );
  const revertToShipped = useCallback<ShipmentsContextType['revertToShipped']>(
    async (shipmentId) => {
      await revertToShippedApiCall({}, { url: `shop/shipment/${shipmentId}/revert-to-shipped` });
    },
    [revertToShippedApiCall],
  );
  const updateShipment = useCallback<ShipmentsContextType['updateShipment']>(
    async (shipmentId, body) => updateShipmentApiCall(body, { url: `shop/shipment/${shipmentId}` }),
    [updateShipmentApiCall],
  );
  const deleteShipment = useCallback<ShipmentsContextType['deleteShipment']>(
    async (shipmentId) => {
      await deleteShipmentApiCall({ url: `shop/shipment/${shipmentId}` });
    },
    [deleteShipmentApiCall],
  );

  const value = useMemo<ShipmentsContextType>(
    () => ({
      markReady,
      markShipped,
      markDelivered,
      revertToDraft,
      revertToReady,
      revertToShipped,
      updateShipment,
      deleteShipment,
      mutationRunning:
        markingReady ||
        markingShipped ||
        markingDelivered ||
        revertingToDraft ||
        revertingToReady ||
        revertingToShipped ||
        saving ||
        deleting,
    }),
    [
      markReady,
      markShipped,
      markDelivered,
      revertToDraft,
      revertToReady,
      revertToShipped,
      updateShipment,
      deleteShipment,
      markingReady,
      markingShipped,
      markingDelivered,
      revertingToDraft,
      revertingToReady,
      revertingToShipped,
      saving,
      deleting,
    ],
  );

  return <ShipmentsContext value={value}>{children}</ShipmentsContext>;
};

export const useShipments = () => useGeneralContext(ShipmentsContext, 'Shipments');
